import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useDoctors } from "@/hooks/useDoctors";

export default function DoctorSelection({
  selectedService,
  selectedDate,
  allSlots,
  doctorFilter,
  loadingSlots,
  onDoctorFilterChange,
  practiceId = null, // Allow specifying practice ID for patients
}) {

  // ALL HOOKS MUST BE CALLED BEFORE ANY EARLY RETURNS

  // Fetch all doctors for the practice (or specified practice for patients)
  // When we have a service, fetch doctors who can perform that service
  const { data: allDoctors = [], isLoading: doctorsLoading } = useDoctors(
    practiceId,
    selectedService?.id
  );

  // Calculate available doctors - this useMemo must be called before early returns
  const availableDoctors = useMemo(() => {
    if (!allSlots || allSlots.length === 0) {
      return [];
    }

    // Get unique doctor IDs from available slots
    const doctorIdsWithSlots = Array.from(
      new Set(
        allSlots.filter((slot) => slot.doctorId).map((slot) => slot.doctorId)
      )
    );

    // Filter active doctors with available slots (patients only see active doctors)
    return allDoctors
      .filter(
        (doctor) =>
          doctor.status === "active" && doctorIdsWithSlots.includes(doctor.id)
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allDoctors, allSlots]);

  // Only show doctor dropdown when service requires doctor and date is selected
  const serviceRequiresDoctor = selectedService?.requires_doctor;
  const hasSelectedDate = !!selectedDate;


  if (!serviceRequiresDoctor || !hasSelectedDate) {
    return null;
  }

  if (availableDoctors.length === 0) {
    return null;
  }

  if (doctorsLoading) {
    return (
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select Doctor *</Label>
        <div className="w-full h-10 bg-gray-100 animate-pulse rounded-md"></div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <Label className="text-sm font-medium">Select Doctor *</Label>

      {loadingSlots ? (
        <div className="w-full p-3 border border-slate-200 rounded-md bg-slate-50 text-slate-500 flex items-center gap-2">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading available doctors...
        </div>
      ) : availableDoctors.length > 0 ? (
        <Select
          key={selectedDate ? selectedDate.toISOString() : "no-date"}
          value={doctorFilter || undefined}
          onValueChange={onDoctorFilterChange}
        >
          <SelectTrigger className="w-full">
            <SelectValue placeholder="Choose a doctor" />
          </SelectTrigger>
          <SelectContent>
            {availableDoctors.map((doctor) => (
              <SelectItem key={doctor.id} value={doctor.id}>
                {doctor.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div className="w-full p-3 border border-slate-200 rounded-md bg-slate-50 text-slate-500">
          No doctors available for this time slot
        </div>
      )}

      {!doctorFilter && availableDoctors.length > 0 && (
        <p className="text-xs text-red-500">
          Please select a doctor to see available appointment times
        </p>
      )}
    </div>
  );
}
